import { KV } from 'types/utils';
import { RobotsContent } from '@navi-toolbox/seo/types/meta-tag/robots';
import { FaqProps } from 'features/faq/types';

export type FetchModelProps = {
  regionCode: string;
  pathname: string;
  requiredFields: string;
};

export type BasicLinkProps = {
  label: string;
  link: string;
};

export type GetModelResponse = {
  statusCode: number | null;
} & Partial<GetModelErrorResponse> &
  Partial<{
    visibilityData: GetModelVisibility | null;
    modelJson: ModelJson;
  }>;

export type GetModelErrorResponse = {
  code: string;
  error: string;
  message: string;
};

export type GetModelVisibility = {
  responseStatus: number | null;
  visibility: boolean | null;
  visibilityErrors: {
    message: string;
  }[];
};

export type RegionModel = {
  regionId: string;
  regionCode: string;
  regionName: string;
  currentPageLink: string;
};

export type StretchBannerModel = {
  gaName: string;
  gaPosition: string;
  height: number;
  width: number;
  placementId: string;
  closeButton: boolean;
  autohide: boolean;
  autohideViews: number;
  targetBlank: boolean;
  viewingTime: number;
};

export type KindAnchor = {
  text: string;
  href: string;
  target?: string;
};

export type KindDivisionsList = string[];

export type KindSourceList = string[];

export type YaSplitInfo = {
  availablePaymentMethods: string[];
  borderRadius: number;
  hideWidgetHeader: boolean;
  src: string;
  enabled: boolean;
  env: string;
  version: number;
  merchantId: string;
  maxPriceThreshold: number;
  linkPage: KindAnchor;
  logoLink: string;
  KindDivisionsList: KindDivisionsList;
  KindSourceList: KindSourceList;
  withOutline: boolean;
  widgetSize: string;
  widgetBackground: string;
  widgetTheme: string;
  widgetType: string;
  padding: string;
};

export type OriginalProductType = {
  enable: boolean;
  text: string;
  color: string;
  excludeFamilies: string[];
  tooltip: {
    title: string;
    text: string;
    buttonText: string;
  };
};

export type UniqueOfferModalType = {
  title: string;
  text: string;
  icon: string;
  button: { text: string; href?: string; target: string };
};

export type UniqueOfferItemType = {
  text: string;
  icon: string;
  modal: UniqueOfferModalType;
};

export type UniqueOfferType = {
  title: string;
  enable: boolean;
  items: UniqueOfferItemType[];
};

export type ModelJson = {
  properties: {
    title: string;
    regionId: string;
    familyId: string;
    hideBreadcrumbs: boolean;
    params: KV | null;
    sort: KV | null;
    facetWrapNumber: string;
  };
  seoProperties: SEOProperties;
  experiments?: Experiment[];
  header: {
    register: string;
    account: string;
    advices: string;
    shops: string;
    suppliers: string;
    faq: string;
    home: string;
    shoppingList: string;
    hideMobileRegion: boolean | null;
    hideBreadcrumbs: boolean;
    hideTopBanner: boolean | null;
    logoutUrl: string;
    logoutResource: string;
    initUrl: string;
    forgotPassword: string;
    logoImage: string;
    faviconImage: string;
    linkToCompare: string;
    linkToSearch: string;
    cookieLink: string;
    linkToShoppingList: string;
    recaptchaPublicKey: string;
  };
  breadcrumbs: Breadcrumb[];
  brand: {
    label: string | null;
    logo: {
      originalLink: string | null;
      cloudinaryLink: string | null;

      leftsideImgSrc?: string;
      rightsideImgSrc?: string;
    };
    pagePath: string | null;
  };
  analytics?: {
    gtmCode?: string;
    yaCode?: string;
  };
  warningMessage: string | null;
  regions: RegionModel[];
  rmBannerRastyazhka?: StretchBannerModel;
  yaSplitInfo?: YaSplitInfo;
  originalProduct: OriginalProductType;
  faq: FaqProps;
  uniqueOffer: UniqueOfferType;
};

export type SEOProperties = {
  metadescription: string | null;
  metakeywords: string | null;
  seotext: string | null;
  pageTitle: string;
  h1: string;
  canonicalURL: string | null;
  seoTemplate: string;
  vanityPath: string | null;
  canonicalURLNeeded: boolean;
  metaRobotsContent?: RobotsContent[];
};

export const AllowedSEOTemplates = [
  'h1',
  'metadescription',
  'metakeywords',
  'pageTitle',
  'seotext',
] as const;
export type PopulateSeoTemplatesFor = (typeof AllowedSEOTemplates)[number];
export type SEOTemplates = Partial<Record<PopulateSeoTemplatesFor, string>>;
export type SeoTemplateVariables = 'displayedName' | 'price' | 'parent' | 'parent_link' | 'LMCode';

export type Breadcrumb = BasicLinkProps;

export type Experiment = {
  internalId: string;
  externalId?: string;
};
